import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { Organisations } from 'server/shared/collections/organisations';
import { randomId } from 'server/shared/helpers/random';
import { Certificate } from 'server/shared/models/certificate';
import { PerigonTaskMapping } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { Organisation } from 'server/shared/models/organisation';
import { Shift } from 'server/shared/models/shift';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { MappedCertificate } from 'server/shared/models/user';
import { observeTracker } from 'src/helpers/meteorObservable/observeTracker';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { CustomerService } from '../../services/customer.service';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { SubscriptionService } from '../../shared/subscription.service';

type PerigonTaskMappingsInCreation = {
  taskId: string;
  departmentId: string;
  shiftId: string;
  certificates: MappedCertificate[];
};

interface DepartmentWithCollapsed extends Department {
  collapsed: boolean;
}

interface OrganisationWithCollapsed extends Organisation {
  departments: DepartmentWithCollapsed[];
}

@Component({
  selector: 'admin-certificate-groups',
  templateUrl: 'perigon-mapping.component.html',
  styleUrls: ['perigon-mapping.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerigonMappingComponent implements OnDestroy {
  i18n = this.translationService.getTranslations();
  customer$ = this.customerService.readyDispoCustomer$.pipe(shareReplay(1));
  shiftEnum = ShiftEnum;

  private destroyed$ = new Subject<void>();

  perigonTaskMappings$ = new BehaviorSubject<PerigonTaskMapping[]>([]);
  initialPerigonTaskMappings: PerigonTaskMapping[] = [];
  perigonTaskMappingsInCreation$ = new Map<
    string,
    BehaviorSubject<PerigonTaskMappingsInCreation[]>
  >();
  perigonClientIds$ = new Map<string, BehaviorSubject<number | undefined>>();
  originalPerigonClientIds$ = new Map<string, number | undefined>();

  searchTextInputValue$ = new BehaviorSubject<string>('');
  searchText$ = this.searchTextInputValue$.pipe(
    distinctUntilChanged(),
    shareReplay(1),
  );

  customerId$ = this.customer$.pipe(
    map((customer) => customer?._id),
    distinctUntilChanged(),
  );

  searchQuery$ = combineLatest([this.customerId$, this.searchText$]).pipe(
    map(([customerId, searchText]) => {
      if (!searchText) {
        return;
      }
      const filters: Record<string, any> = {
        $or: [],
      };
      let searchTexts = searchText.replace('+', '\\+').split(' ');
      searchTexts.forEach((searchText) => {
        filters['$or'].push({
          $or: [
            {
              name: {
                $regex: searchText,
                $options: 'i',
              },
            },
            {
              'departments.name': {
                $regex: searchText,
                $options: 'i',
              },
            },
          ],
        });
      });
      return filters;
    }),
  );

  organisationsLoading$ = this.subscriptionService.organisations.loading$.pipe(
    rxNgZoneRun(this.ngZone),
    shareReplay(1),
  );

  organisations$ = this.subscriptionService.organisations.ready$.pipe(
    switchMap(() => {
      return combineLatest([
        this.searchQuery$.pipe(
          switchMap((searchQuery) =>
            observeTracker(() => {
              return Organisations.find(searchQuery || {}, {
                sort: { name: 1 },
              }).fetch();
            }),
          ),
        ),
      ]).pipe(
        map(([organisations]) => {
          return organisations.map(
            (organisation): OrganisationWithCollapsed => ({
              ...organisation,
              departments: organisation.departments.map((department) => {
                return {
                  ...department,
                  collapsed: true,
                };
              }),
            }),
          );
        }),
      );
    }),
    rxNgZoneRun(this.ngZone),
    shareReplay(1),
  );

  constructor(
    private toastService: ToastService,
    private ngZone: NgZone,
    private translationService: TranslationService,
    private customerService: CustomerService,
    private permissionService: PermissionsService,
    private subscriptionService: SubscriptionService,
  ) {
    this.customer$.pipe(takeUntil(this.destroyed$)).subscribe((customer) => {
      if (customer) {
        this.initializePerigonShiftMappings();
      }
    });
    this.permissionService.isCustomerAdmin$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCustomerAdmin) => {
        if (!isCustomerAdmin) {
          this.historyBack();
        }
      });
  }

  async initializePerigonShiftMappings() {
    const customer = await firstValueFrom(this.customer$);
    if (!customer) return;

    let perigonTaskMappingsFromCustomer =
      customer.perigonMappings?.perigonTaskMappings || [];

    if (this.perigonTaskMappings$.value.length === 0) {
      this.perigonTaskMappings$.next([...perigonTaskMappingsFromCustomer]);
    } else {
      let currentPerigonTaskMappings = [...this.perigonTaskMappings$.value];
      perigonTaskMappingsFromCustomer.forEach(
        (perigonTaskMappingFromCustomer: PerigonTaskMapping) => {
          let perigonTaskMappingIndex = currentPerigonTaskMappings.findIndex(
            (perigonTaskMapping) => {
              return (
                perigonTaskMapping.id === perigonTaskMappingFromCustomer.id
              );
            },
          );
          if (perigonTaskMappingIndex === -1) {
            currentPerigonTaskMappings.push(perigonTaskMappingFromCustomer);
          }
          if (
            this.initialPerigonTaskMappings[perigonTaskMappingIndex] &&
            currentPerigonTaskMappings[perigonTaskMappingIndex].id ===
              this.initialPerigonTaskMappings[perigonTaskMappingIndex].id
          ) {
            currentPerigonTaskMappings[perigonTaskMappingIndex] =
              perigonTaskMappingFromCustomer;
          }
        },
      );
      this.perigonTaskMappings$.next(currentPerigonTaskMappings);
    }

    this.initialPerigonTaskMappings = [
      ...JSON.parse(JSON.stringify(perigonTaskMappingsFromCustomer)),
    ];

    // Initialize client IDs for departments
    const clientIdsByDepartment =
      customer.perigonMappings?.perigonClientsByDepartmentId || {};
    Object.keys(clientIdsByDepartment).forEach((departmentId) => {
      if (!this.perigonClientIds$.has(departmentId)) {
        this.originalPerigonClientIds$.set(
          departmentId,
          clientIdsByDepartment[departmentId]?.id,
        );
        this.perigonClientIds$.set(
          departmentId,
          new BehaviorSubject<number | undefined>(
            clientIdsByDepartment[departmentId]?.id,
          ),
        );
      } else {
        this.perigonClientIds$
          .get(departmentId)!
          .next(clientIdsByDepartment[departmentId]?.id);
        this.originalPerigonClientIds$.set(
          departmentId,
          clientIdsByDepartment[departmentId]?.id,
        );
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  historyBack() {
    window.history.back();
  }

  addNewPerigonTaskMapping(departmentId: string, shiftId: string) {
    const key = `${departmentId}-${shiftId}`;
    if (!this.perigonTaskMappingsInCreation$.has(key)) {
      this.perigonTaskMappingsInCreation$.set(
        key,
        new BehaviorSubject<PerigonTaskMappingsInCreation[]>([]),
      );
    }
    const currentSubject = this.perigonTaskMappingsInCreation$.get(key)!;
    const current = [...currentSubject.value];
    current.push({
      taskId: '',
      departmentId: departmentId,
      shiftId: shiftId,
      certificates: [],
    });
    currentSubject.next(current);
    // Trigger change detection by updating the observable
    this.perigonTaskMappings$.next([...this.perigonTaskMappings$.value]);
  }

  cancelNewPerigonTaskMapping(
    departmentId: string,
    shiftId: string,
    index: number,
  ) {
    const key = `${departmentId}-${shiftId}`;
    const currentSubject = this.perigonTaskMappingsInCreation$.get(key);
    if (!currentSubject) return;
    const current = [...currentSubject.value];
    current.splice(index, 1);
    currentSubject.next(current);
  }

  getPerigonTaskMappingsInCreation$(
    departmentId: string,
    shiftId: string,
  ): BehaviorSubject<PerigonTaskMappingsInCreation[]> {
    const key = `${departmentId}-${shiftId}`;
    if (!this.perigonTaskMappingsInCreation$.has(key)) {
      this.perigonTaskMappingsInCreation$.set(
        key,
        new BehaviorSubject<PerigonTaskMappingsInCreation[]>([]),
      );
    }
    return this.perigonTaskMappingsInCreation$.get(key)!;
  }

  getPerigonTaskMappingsForDepartmentAndShift(
    departmentId: string,
    shiftId: string,
  ): PerigonTaskMapping[] {
    return this.perigonTaskMappings$.value.filter(
      (mapping) =>
        mapping.departmentId === departmentId && mapping.shiftId === shiftId,
    );
  }

  getShiftsForDepartment(
    organisation: Organisation,
    departmentId: string,
  ): Shift[] {
    const department = organisation.departments.find(
      (d) => d.id === departmentId,
    );
    if (!department) return [];

    const mappings = this.perigonTaskMappings$.value.filter(
      (mapping) => mapping.departmentId === departmentId,
    );
    const shiftIds = new Set(mappings.map((m) => m.shiftId));
    // Also include shifts that are in creation
    this.perigonTaskMappingsInCreation$.forEach((subject, key) => {
      if (key.startsWith(`${departmentId}-`)) {
        const shiftId = key.split('-').slice(1).join('-');
        shiftIds.add(shiftId);
      }
    });

    return [...department.shifts];
  }

  getShiftById(department: Department, shiftId: string): Shift | undefined {
    return department.shifts.find((shift) => shift.id === shiftId);
  }

  getShiftDisplayName(department: Department, shift: Shift): string {
    if (shift.label) {
      return shift.label;
    }
    return (
      this.i18n.shifts?.[shift.shift] || ShiftEnum[shift.shift] || shift.id
    );
  }

  isShiftAlreadyAdded(
    organisation: Organisation,
    departmentId: string,
    shiftId: string,
  ): boolean {
    const shifts = this.getShiftsForDepartment(organisation, departmentId);
    return shifts.some((shift) => shift.id === shiftId);
  }

  getPerigonClientIdForDepartment$(
    departmentId: string,
  ): BehaviorSubject<number | undefined> {
    if (!this.perigonClientIds$.has(departmentId)) {
      this.perigonClientIds$.set(
        departmentId,
        new BehaviorSubject<number | undefined>(undefined),
      );
    }
    return this.perigonClientIds$.get(departmentId)!;
  }

  async updatePerigonClientIdForDepartment(
    departmentId: string,
    clientIdString: string,
  ): Promise<void> {
    const customer = await firstValueFrom(this.customer$);
    if (!customer) return;

    let previousClientId = this.originalPerigonClientIds$.get(departmentId);

    const clientId =
      clientIdString && clientIdString.trim() !== ''
        ? parseInt(clientIdString, 10)
        : null;

    if (previousClientId === clientId) {
      return;
    }

    if (clientId !== null && isNaN(clientId)) {
      this.toastService.add({
        type: 'ERROR',
        message: 'Invalid client ID',
      });
      return;
    }

    Meteor.call(
      'updatePerigonClientIdForDepartment',
      customer._id,
      departmentId,
      clientId,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
          // Refresh the customer data
          this.initializePerigonShiftMappings();
        }
      },
    );
  }

  async saveNewPerigonTaskMapping(
    departmentId: string,
    shiftId: string,
    index: number,
  ) {
    const key = `${departmentId}-${shiftId}`;
    const currentSubject = this.perigonTaskMappingsInCreation$.get(key);
    if (!currentSubject) return;

    let taskId = currentSubject.value[index]?.taskId;
    let certificates = currentSubject.value[index]?.certificates;
    const customer = await firstValueFrom(this.customer$);
    if (!taskId || !shiftId || !customer) return;

    let newPerigonTaskMapping: PerigonTaskMapping = {
      id: randomId(),
      perigonTaskId: taskId,
      departmentId: departmentId,
      shiftId: shiftId,
      certificates: certificates || [],
    };

    const currentPerigonTaskMappings = [...this.perigonTaskMappings$.value];
    currentPerigonTaskMappings.push(newPerigonTaskMapping);
    this.perigonTaskMappings$.next(currentPerigonTaskMappings);

    const currentInCreation = [...currentSubject.value];
    currentInCreation.splice(index, 1);
    currentSubject.next(currentInCreation);

    Meteor.call(
      'addPerigonTaskMapping',
      customer._id,
      newPerigonTaskMapping,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  async deletePerigonTaskMapping(perigonTaskMappingId: string) {
    const customer = await firstValueFrom(this.customer$);
    if (!customer || !perigonTaskMappingId) return;

    const currentPerigonTaskMappings = [...this.perigonTaskMappings$.value];
    let index = currentPerigonTaskMappings.findIndex((perigonTaskMapping) => {
      return perigonTaskMapping.id === perigonTaskMappingId;
    });
    currentPerigonTaskMappings.splice(index, 1);
    this.perigonTaskMappings$.next(currentPerigonTaskMappings);

    Meteor.call(
      'removePerigonTaskMapping',
      customer._id,
      perigonTaskMappingId,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  cancelChanges(perigonTaskMappingId: string) {
    let index = this.initialPerigonTaskMappings.findIndex(
      (perigonTaskMapping) => {
        return perigonTaskMapping.id === perigonTaskMappingId;
      },
    );

    const currentPerigonTaskMappings = [...this.perigonTaskMappings$.value];
    currentPerigonTaskMappings[index].id =
      this.initialPerigonTaskMappings[index].id;
    currentPerigonTaskMappings[index].perigonTaskId =
      this.initialPerigonTaskMappings[index].perigonTaskId;
    currentPerigonTaskMappings[index].departmentId =
      this.initialPerigonTaskMappings[index].departmentId;
    currentPerigonTaskMappings[index].shiftId =
      this.initialPerigonTaskMappings[index].shiftId;
    currentPerigonTaskMappings[index].certificates =
      this.initialPerigonTaskMappings[index].certificates;
    this.perigonTaskMappings$.next(currentPerigonTaskMappings);
  }

  async savePerigonTaskMapping(perigonTaskMappingId: string): Promise<void> {
    let perigonTaskMapping = this.perigonTaskMappings$.value.find(
      (perigonTaskMapping) => {
        return perigonTaskMapping.id === perigonTaskMappingId;
      },
    );
    const customer = await firstValueFrom(this.customer$);
    if (!customer || !perigonTaskMapping) return;

    Meteor.call(
      'updatePerigonTaskMapping',
      customer._id,
      perigonTaskMapping,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  certificatesSelected(
    perigonTaskMapping: PerigonTaskMapping | PerigonTaskMappingsInCreation,
    certificates: Certificate[],
  ) {
    perigonTaskMapping.certificates = certificates.map((certificate) => {
      return {
        id: certificate._id,
        displayName: certificate.name,
      };
    });
  }

  trackById(index: number, object: PerigonTaskMapping): string {
    return object.id;
  }

  trackByOrganisationId(index: number, organisation: Organisation): string {
    return organisation._id;
  }

  trackByDepartmentId(index: number, department: Department): string {
    return department.id;
  }

  trackByShiftId(index: number, shift: Shift): string {
    return shift.id;
  }

  searchFromInput(searchText: string): void {
    this.searchTextInputValue$.next(searchText);
  }
}
